import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AnyAction, AsyncThunk } from '@reduxjs/toolkit';
import { syncActions } from '.';
import { analyticsThunkActions } from '../analytics';
import { caregiverThunkActions } from '../caregiver';
import { documentSelectors, documentThunkActions } from '../document';
import { motherSelectors, motherThunkActions } from '../mother';
import { infantSelectors, infantThunkActions } from '../infant';
import { userThunkActions } from '../user';

import type { RootState, ThunkApiType } from '../types';
import type {
  SyncOfflineDataProps,
  SyncOfflineDataReturnType,
} from './sync.types';
import { queryErrorsThunkActions } from '../queryErrors';
import { authSelectors } from '../auth';

type SyncStep = {
  title: string;
  action: AsyncThunk<any, any, any>;
  // clearAction?: () => AnyAction;  ← currently unused → consider removing or implementing
};

const isNetworkError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;

  return (
    // Common fetch/axios-like network errors
    !('response' in error) &&
    (error.message.includes('Network') ||
      error.message.includes('Failed to fetch') ||
      error.message.includes('Network request failed') ||
      error.name === 'TimeoutError' ||
      (error as any).code === 'ECONNABORTED')
  );
};

const retryWithExponentialBackoff = async <T>(
  fn: () => Promise<T>,
  options: {
    maxAttempts?: number;
    baseDelayMs?: number;
    onRetry?: (attempt: number, error: unknown) => void;
  } = {}
): Promise<T> => {
  const { maxAttempts = 3, baseDelayMs = 1000, onRetry } = options;

  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;

      if (!isNetworkError(err) || attempt === maxAttempts) {
        throw err;
      }

      const delay = baseDelayMs * 2 ** (attempt - 1); // 1s → 2s → 4s ...
      onRetry?.(attempt, err);
      await new Promise((r) => setTimeout(r, delay));
    }
  }

  throw lastError;
};

// ────────────────────────────────────────────────

export const syncOfflineData = createAsyncThunk<
  SyncOfflineDataReturnType,
  SyncOfflineDataProps | void, // allow calling without args
  ThunkApiType<RootState>
>('sync/offlineData', async (_, { getState, rejectWithValue, dispatch }) => {
  const syncSteps: SyncStep[] = [
    { title: 'User', action: userThunkActions.updateUser },
    { title: 'Caregivers', action: caregiverThunkActions.upsertCareGivers },
    { title: 'Mothers', action: motherThunkActions.upsertMothers },
    { title: 'Infants', action: infantThunkActions.upsertInfants },
    { title: 'User Consents', action: userThunkActions.upsertUserConsents },
    { title: 'Analytics', action: analyticsThunkActions.pushAnalytics },
    { title: 'Documents', action: documentThunkActions.upsertDocument },
    { title: 'Infant Visits', action: infantThunkActions.syncInfantVisits },
    {
      title: 'Infant Events',
      action: infantThunkActions.syncInfantEventRecords,
    },
    { title: 'Mother Visits', action: motherThunkActions.syncMotherVisits },
    {
      title: 'Mother Events',
      action: motherThunkActions.syncMotherEventRecords,
    },
    {
      title: 'API Errors',
      action: queryErrorsThunkActions.upsertQueryErrors,
    },
  ];

  let hasCriticalError = false;
  let firstError: unknown = null;

  for (let i = 0; i < syncSteps.length; i++) {
    const { title, action } = syncSteps[i];

    dispatch(
      syncActions.setCurrentActionState({
        title,
        step: i + 1,
        stepTotal: syncSteps.length,
      })
    );

    try {
      await retryWithExponentialBackoff(() => dispatch(action({})).unwrap(), {
        maxAttempts: 3,
        baseDelayMs: 1000,
        onRetry: (attempt, err) => {
          console.warn(`[${title}] Network retry ${attempt}/3 after:`, err);
        },
      });
    } catch (err) {
      console.error(`Sync failed at "${title}":`, err);

      // Keep first error only (most meaningful one usually)
      if (!firstError) {
        firstError = err;
      }

      dispatch(
        syncActions.setError(
          err instanceof Error ? err.message : 'Unknown sync error'
        )
      );

      // Most steps are critical → stop on first real error
      hasCriticalError = true;
      break;
    }
  }

  if (hasCriticalError) {
    return rejectWithValue(
      firstError instanceof Error ? firstError.message : 'Offline sync failed'
    );
  }

  return {}; // or whatever meaningful success value you want
});

export const triggerBackgroundSync = createAsyncThunk<
  void,
  {},
  ThunkApiType<RootState>
>(
  'sync/triggerBackgroundSync',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const currentState = getState();

      const user = authSelectors.getAuthUser(currentState);
      const docsToSync =
        documentSelectors.getDocumentsToSync(currentState) || [];

      // Infant checks
      const infantsToSync =
        infantSelectors.getInfantsToSync(currentState) || [];
      const infantVisitsToSync =
        infantSelectors.getInfantVisitsToSync(currentState) || [];
      const infantEventRecordsToSync =
        infantSelectors.getInfantRecordEventsToSync(currentState) || [];

      // Mother checks
      const mothersToSync =
        motherSelectors.getMothersToSync(currentState) || [];
      const motherVisitsToSync =
        motherSelectors.getMotherVisitsToSync(currentState) || [];
      const motherEventRecordsToSync =
        motherSelectors.getMotherRecordEventsToSync(currentState) || [];

      const hasItemsToSync =
        !!user?.auth_token &&
        (infantsToSync.length > 0 ||
          infantVisitsToSync.length > 0 ||
          infantEventRecordsToSync.length > 0 ||
          mothersToSync.length > 0 ||
          motherVisitsToSync.length > 0 ||
          motherEventRecordsToSync.length > 0 ||
          docsToSync.length > 0);

      if (hasItemsToSync) {
        await dispatch(syncOfflineData()).unwrap();
      }
    } catch (err: any) {
      dispatch(syncActions.setError(err?.message ?? 'Background sync error'));
      return rejectWithValue(err);
    }
  }
);
